import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, TextInput, TextArea } from "@/components/admin/fields";

const DEFAULT = {
  image: "",
  overline: "",
  title_line1: "",
  title_emphasis: "",
  title_line2: "",
  title_accent: "",
  title_suffix: ".",
  subtitle: "",
  subtitle_bold: "",
  image_caption_left: "",
  image_caption_right: "",
  stats: [
    { value: "", label: "" },
    { value: "", label: "" },
    { value: "", label: "" },
  ],
};

export default function HeroPanel() {
  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/site/hero")
      .then((r) => {
        const data = r.data || {};
        setForm({
          ...DEFAULT,
          ...data,
          stats:
            Array.isArray(data.stats) && data.stats.length > 0
              ? data.stats
              : DEFAULT.stats,
        });
      })
      .catch((err) =>
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Error al cargar Hero"
        )
      )
      .finally(() => setLoading(false));
  }, []);

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setStat = (i, patch) =>
    setForm((f) => ({
      ...f,
      stats: f.stats.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
  const addStat = () =>
    setForm((f) => ({ ...f, stats: [...f.stats, { value: "", label: "" }] }));
  const removeStat = (i) =>
    setForm((f) => ({ ...f, stats: f.stats.filter((_, idx) => idx !== i) }));

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/hero", form);
      toast.success("Hero actualizado");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "No se pudo guardar"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-[#525252]">Cargando…</div>;
  }

  return (
    <div data-testid="hero-panel" className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold tracking-tighter text-2xl md:text-3xl">
            Primer bloque (Hero)
          </h2>
          <p className="mt-1 text-sm text-[#525252]">
            Imagen y textos de la sección principal del sitio.
          </p>
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="hero-save"
          className="btn-primary text-[0.7rem] px-4 py-3 disabled:opacity-60"
        >
          {saving ? (
            <>
              <Loader2 size={14} strokeWidth={1.8} className="animate-spin" />{" "}
              Guardando
            </>
          ) : (
            <>
              <Save size={14} strokeWidth={1.8} /> Guardar
            </>
          )}
        </button>
      </div>

      <section className="bg-white border border-black/8 p-6 space-y-5">
        <div className="flex items-center gap-3 pb-3 border-b border-black/8">
          <div className="w-9 h-9 flex items-center justify-center bg-[#0a0a0a] text-white">
            <ImageIcon size={15} strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-display font-semibold text-lg tracking-tight">
              Imagen principal
            </div>
            <div className="text-xs text-[#525252]">
              URL pública de la foto que aparece a la derecha del título.
            </div>
          </div>
        </div>

        <Field label="URL de la imagen" required>
          <TextInput
            value={form.image}
            onChange={(v) => setField({ image: v })}
            placeholder="https://…"
          />
        </Field>
        {form.image && (
          <div className="border border-black/10 p-2 bg-[#fafafa]">
            <img
              src={form.image}
              alt="preview"
              className="w-full aspect-[3/4] object-cover max-w-xs"
            />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Texto inferior izquierdo (sobre imagen)">
            <TextInput
              value={form.image_caption_left}
              onChange={(v) => setField({ image_caption_left: v })}
              placeholder="Edición 2026"
            />
          </Field>
          <Field label="Texto inferior derecho (sobre imagen)">
            <TextInput
              value={form.image_caption_right}
              onChange={(v) => setField({ image_caption_right: v })}
              placeholder="Inscripciones abiertas"
            />
          </Field>
        </div>
      </section>

      <section className="bg-white border border-black/8 p-6 space-y-5">
        <div className="pb-3 border-b border-black/8">
          <div className="font-display font-semibold text-lg tracking-tight">
            Encabezado y título
          </div>
          <div className="text-xs text-[#525252]">
            El título se construye con cuatro partes; las dos palabras
            destacadas se muestran en cursiva y rojo respectivamente.
          </div>
        </div>

        <Field label="Texto superior pequeño (Overline)">
          <TextInput
            value={form.overline}
            onChange={(v) => setField({ overline: v })}
            placeholder="Academia de Belleza · Puebla"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Línea 1 del título">
            <TextInput
              value={form.title_line1}
              onChange={(v) => setField({ title_line1: v })}
              placeholder="Conviértete en un"
            />
          </Field>
          <Field label="Palabra destacada en cursiva">
            <TextInput
              value={form.title_emphasis}
              onChange={(v) => setField({ title_emphasis: v })}
              placeholder="profesional"
            />
          </Field>
          <Field label="Línea 2 del título">
            <TextInput
              value={form.title_line2}
              onChange={(v) => setField({ title_line2: v })}
              placeholder="de la"
            />
          </Field>
          <Field label="Palabra destacada en rojo">
            <TextInput
              value={form.title_accent}
              onChange={(v) => setField({ title_accent: v })}
              placeholder="belleza"
            />
          </Field>
        </div>

        <Field label="Sufijo del título" hint='Normalmente solo un punto "."'>
          <TextInput
            value={form.title_suffix}
            onChange={(v) => setField({ title_suffix: v })}
            placeholder="."
          />
        </Field>
      </section>

      <section className="bg-white border border-black/8 p-6 space-y-5">
        <div className="pb-3 border-b border-black/8">
          <div className="font-display font-semibold text-lg tracking-tight">
            Descripción
          </div>
          <div className="text-xs text-[#525252]">
            Donde escribas <code className="px-1 bg-[#fafafa]">{"{bold}"}</code>{" "}
            aparecerá la frase resaltada.
          </div>
        </div>
        <Field label="Texto descriptivo">
          <TextArea
            rows={4}
            value={form.subtitle}
            onChange={(v) => setField({ subtitle: v })}
            placeholder="Formación premium {bold}: estilismo, barbería…"
          />
        </Field>
        <Field label="Frase resaltada en negrita">
          <TextInput
            value={form.subtitle_bold}
            onChange={(v) => setField({ subtitle_bold: v })}
            placeholder="a tu alcance"
          />
        </Field>
      </section>

      <section className="bg-white border border-black/8 p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 pb-3 border-b border-black/8">
          <div>
            <div className="font-display font-semibold text-lg tracking-tight">
              Estadísticas
            </div>
            <div className="text-xs text-[#525252]">
              Hasta 3 elementos. El sufijo no numérico (k, +) se muestra en
              rojo automáticamente.
            </div>
          </div>
          {form.stats.length < 3 && (
            <button
              type="button"
              onClick={addStat}
              className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] font-semibold px-3 py-2 border border-black/15 hover:bg-[#0a0a0a] hover:text-white transition-colors"
            >
              <Plus size={13} strokeWidth={1.6} /> Añadir
            </button>
          )}
        </div>
        <div className="space-y-3">
          {form.stats.map((s, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-[150px_1fr_auto] gap-3 items-end border border-black/10 p-3 bg-[#fafafa]"
            >
              <Field label="Valor">
                <TextInput
                  value={s.value}
                  onChange={(v) => setStat(i, { value: v })}
                  placeholder="+12k"
                />
              </Field>
              <Field label="Etiqueta">
                <TextInput
                  value={s.label}
                  onChange={(v) => setStat(i, { label: v })}
                  placeholder="Egresados"
                />
              </Field>
              <button
                type="button"
                onClick={() => removeStat(i)}
                className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#525252] hover:bg-[#c40000] hover:text-white hover:border-[#c40000] transition-colors"
                aria-label="Eliminar"
              >
                <Trash2 size={13} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="btn-primary text-[0.7rem] px-4 py-3 disabled:opacity-60"
        >
          {saving ? (
            <>
              <Loader2 size={14} strokeWidth={1.8} className="animate-spin" />{" "}
              Guardando
            </>
          ) : (
            <>
              <Save size={14} strokeWidth={1.8} /> Guardar cambios
            </>
          )}
        </button>
      </div>
    </div>
  );
}
